import axios, {
  AxiosDefaults,
  AxiosInterceptorManager,
  AxiosRequestConfig,
} from 'axios'

import { injectBearer } from '../utils/bearer'
import Session from '../Session'
import { ShouldSendHeaders } from '../utils/urls'
import { AccessTokenType } from '../utils/tokens'
import useSynchronousEffect from '../utils/useSynchronousEffect'

interface useAuthInterceptorParams {
  disableInterceptor: boolean
  session: Session | null
  shouldSendHeadersHandler: ShouldSendHeaders
  tokenType: AccessTokenType
}

type AxiosDefaultRequestInterceptorManager = AxiosInterceptorManager<
  AxiosDefaults & AxiosRequestConfig
>

const useAuthInterceptor = ({
  disableInterceptor,
  session,
  shouldSendHeadersHandler,
  tokenType,
}: useAuthInterceptorParams) => {
  const canApplyInterceptor = !!session?.accessToken && !disableInterceptor

  useSynchronousEffect(() => {
    if (!canApplyInterceptor) {
      return
    }

    const interceptor = (
      axios.interceptors
        .request as unknown as AxiosDefaultRequestInterceptorManager
    ).use(
      (config) => {
        if (
          !Object.prototype.hasOwnProperty.call(
            config.headers.common,
            'Authorization',
          )
        ) {
          if (
            session?.accessToken &&
            config.url &&
            shouldSendHeadersHandler(config.url) &&
            tokenType === 'Bearer'
          ) {
            config.headers.common.Authorization = injectBearer(
              session.accessToken,
            )
          }
        } else if (!config.headers.Authorization) {
          delete config.headers.Authorization
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      },
    )
    // eject axios interceptor when un-mounting
    return () => {
      axios.interceptors.request.eject(interceptor)
    }
  }, [
    canApplyInterceptor,
    session?.accessToken,
    tokenType,
    shouldSendHeadersHandler,
  ])
}

export default useAuthInterceptor
